import { storeToRefs } from 'pinia'
import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  RouteRecordNormalized
} from 'vue-router'

import { useLayoutStore } from '@/modules/layout/store/layout'
import { useLocationsStore } from '@/modules/locations/store/locations'
import pinia from '@/store'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import type { LocationType } from '@/types'

const PUBLIC_ROUTES = [
  'login',
  'signup',
  'signupOld',
  'supportLogin',
  'redirectLogin',
  'newPassword',
  'rating',
  'createUser'
]

const INSTALLATION_ROUTES = [
  'installation',
  'hardwareStore',
  'hardwareStoreHome',
  'hardwareStoreOrders',
  'hardwareStoreCheckout',
  'setup'
]

const HARDWARE_STORE_ROUTES = ['hardwareStore', 'hardwareStoreHome']

const HOME_ROUTE: RouteLocationRaw = { name: 'home', force: true }

export function getLocationType(): LocationType | undefined {
  const locationsStore = useLocationsStore(pinia)
  const { isSingleLocation, isLocationMode, organizations } = storeToRefs(locationsStore)

  if (Object.keys(organizations.value).length === 0) {
    return undefined
  }

  if (isLocationMode.value) {
    return 'location'
  } else if (isSingleLocation.value) {
    return 'singleLocation'
  }

  return 'organization'
}

export function isRouteAccessibleForLocationType(
  route: RouteRecordNormalized,
  locationType: LocationType
): boolean {
  const locationTypes = route.meta.locationTypes
  if (!locationTypes || locationTypes.length === 0) {
    return true
  }
  return locationTypes.includes(locationType)
}

export function getHomeRoute(): RouteLocationRaw {
  return HOME_ROUTE
}

function getUserPermissions(): string[] {
  const authStore = useAuthStore()
  const { permissions } = storeToRefs(authStore)
  return Object.keys(permissions.value || {})
}

export function authGuard(to: RouteLocationNormalized): true | RouteLocationRaw {
  const auth = useAuthStore(pinia)

  if (PUBLIC_ROUTES.includes(to.name as string)) {
    return true
  }

  if (!auth.isAuthenticated) {
    return {
      name: 'login',
      query: { redirect: to.name ? to.name.toString() : '' },
      force: true
    }
  }

  return true
}

export function installationGuard(to: RouteLocationNormalized): true | RouteLocationRaw {
  const configStore = useConfigStore()
  const { billingStatus } = storeToRefs(configStore)
  const authStore = useAuthStore()
  const { user } = storeToRefs(authStore)

  if (
    !user.value?.isSupport &&
    billingStatus.value?.installation &&
    !INSTALLATION_ROUTES.includes(to.name as string)
  ) {
    return { name: 'installation', force: true }
  }

  return true
}

export function locationTypeGuard(to: RouteLocationNormalized): true | RouteLocationRaw {
  const locationType = getLocationType()

  if (
    locationType &&
    to.meta.locationTypes &&
    to.meta.locationTypes.length > 0 &&
    !to.meta.locationTypes.includes(locationType) &&
    !HARDWARE_STORE_ROUTES.includes(to.name as string)
  ) {
    return HOME_ROUTE
  }

  return true
}

export function permissionsGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalizedLoaded
): true | RouteLocationRaw {
  const layoutStore = useLayoutStore()
  const { showPermissionsErrorOnNextRoute } = storeToRefs(layoutStore)

  const permissions = getUserPermissions()

  if (
    permissions &&
    to.meta.permissions &&
    to.meta.permissions.length > 0 &&
    !permissions.some(permission => to.meta.permissions?.includes(permission)) &&
    !HARDWARE_STORE_ROUTES.includes(to.name as string)
  ) {
    if (
      !from.meta.permissions ||
      (from.meta.permissions.length > 0 &&
        permissions.some(permission => from.meta.permissions?.includes(permission)))
    ) {
      showPermissionsErrorOnNextRoute.value = true
      return from
    }

    if (getLocationType()) return HOME_ROUTE
  }

  return true
}
